import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import { ChevronLeft, ChevronRight, Settings as SettingsIcon, Logout as LogoutIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useContext } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import UserAvatar from '../Avatar/UserAvatar';

/**
 * MuiSidebar - 使用 react-mui-sidebar 库的侧边栏组件
 */
function MuiSidebar({
  courseName,
  courseTermCode,
  navigationItems = [],
  navigationGroups = [],
  userInfo,
  onLogout,
  footerContent,
  children,
  collapsed = false,
  onToggleCollapse,
  onSettingsClick
}) {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);

  const handleLogoutClick = () => {
    setLogoutConfirmOpen(true);
  };

  const handleLogoutConfirm = () => {
    setLogoutConfirmOpen(false);
    onLogout?.();
  };

  const handleLogoutCancel = () => {
    setLogoutConfirmOpen(false);
  };

  // 处理菜单项点击
  const handleMenuItemClick = (item) => {
    if (item.onClick) {
      item.onClick();
    } else if (item.path) {
      navigate(item.path);
    }
  };


  return (
    <Paper
      sx={{
          height: 'calc(100% - 32px)',
          width: '100%',
          maxWidth: collapsed ? '72px' : '270px',
          p: collapsed ? 1 : 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          borderRadius: 2,
          marginTop: 2,
          marginBottom: 2,
          marginLeft: 2,
          marginRight: 2,
          transition: 'all 0.3s ease',
          zIndex: 1300,
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: (theme) =>
            theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.08)',
          backdropFilter: 'blur(6px) saturate(120%)',
          WebkitBackdropFilter: 'blur(6px) saturate(120%)',
          border: '1px solid rgba(255, 255, 255, 0.08)'
        }}
      >
      {/* 侧边栏：透明毛玻璃，背景由全页 FloatingLines 提供 */}
      <Box sx={{ position: 'relative', zIndex: 1, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Top Controls - User Avatar and Toggle Button */}
      <Box sx={{ 
        flexShrink: 0, 
        p: collapsed ? 1.5 : 2,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        mb: 1,
        position: 'relative'
      }}>
        {!collapsed && (
          <>
            <UserAvatar 
              user={user}
              size={72}
              sx={{ fontSize: '1.5rem' }}
            />
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                fontWeight: 500,
                color: 'text.primary',
                textAlign: 'center',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%'
              }}
            >
              {userInfo?.name || user?.name || '用户'}
            </Typography>
          </>
        )}
        {onToggleCollapse && (
          <IconButton 
            size="small" 
            onClick={onToggleCollapse}
            sx={{ 
              color: 'text.secondary',
              width: 28,
              height: 28,
              position: collapsed ? 'relative' : 'absolute',
              top: collapsed ? 0 : 8,
              left: collapsed ? 0 : 8,
              bgcolor: collapsed ? 'transparent' : 'background.paper',
              boxShadow: collapsed ? 'none' : '0 2px 4px rgba(0,0,0,0.1)',
              '&:hover': {
                bgcolor: 'action.hover',
                color: 'text.primary'
              }
            }}
          >
            {collapsed ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
          </IconButton>
        )}
      </Box>

      {/* Menu Items */}
      <Box sx={{ flex: '1 1 auto', overflow: 'auto', minHeight: 0 }}>
        <List sx={{ p: 0 }}>
          {/* Render grouped navigation if available */}
          {navigationGroups.length > 0 ? (
            navigationGroups.map((group, groupIndex) => (
              <React.Fragment key={groupIndex}>
                {/* Group Heading */}
                {!collapsed && group.subHeading && (
                  <Box sx={{ px: 2, py: 1.5, mt: groupIndex > 0 ? 2 : 0 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      {group.subHeading}
                    </Typography>
                  </Box>
                )}
                {/* Group Items */}
                {group.items.map((item, itemIndex) => (
                  <React.Fragment key={itemIndex}>
                    <ListItem disablePadding>
                      <ListItemButton
                        selected={item.selected}
                        onClick={() => handleMenuItemClick(item)}
                        sx={{
                          minHeight: 48,
                          borderRadius: 1,
                          px: collapsed ? 1 : 1.5,
                          py: 1,
                          mb: 0.5,
                          justifyContent: collapsed ? 'center' : 'flex-start',
                          '&:hover': {
                            bgcolor: 'action.hover',
                            borderRadius: 1
                          },
                          '&.Mui-selected': {
                            bgcolor: 'action.selected',
                            color: 'text.primary',
                            borderRadius: 1,
                            '&:hover': {
                              bgcolor: 'action.selected'
                            },
                            '& .MuiListItemIcon-root': {
                              color: 'text.primary'
                            }
                          }
                        }}
                      >
                        {item.icon && (
                          <ListItemIcon sx={{ 
                            minWidth: collapsed ? 0 : 40,
                            justifyContent: collapsed ? 'center' : 'flex-start',
                            mr: collapsed ? 0 : 1
                          }}>
                            {item.icon}
                          </ListItemIcon>
                        )}
                        {!collapsed && (
                          <ListItemText 
                            primary={item.label}
                            primaryTypographyProps={{
                              fontSize: '0.875rem',
                              fontWeight: item.selected ? 500 : 400,
                              fontFamily: 'inherit'
                            }}
                          />
                        )}
                        {item.suffix && !collapsed && (
                          <Box
                            sx={{
                              height: 20,
                              px: 0.75,
                              py: 0,
                              fontSize: '0.7rem',
                              bgcolor: 'primary.main',
                              color: 'primary.contrastText',
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            {item.suffix}
                          </Box>
                        )}
                      </ListItemButton>
                    </ListItem>
                    {/* Expanded Content */}
                    {!collapsed && item.expandedContent && (
                      <Box>
                        {item.expandedContent}
                      </Box>
                    )}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))
          ) : (
            // Fallback to navigationItems for backward compatibility
            navigationItems.map((item, index) => {
            return (
              <React.Fragment key={index}>
                <ListItem disablePadding>
                  <ListItemButton
                    selected={item.selected}
                    onClick={() => handleMenuItemClick(item)}
                    sx={{
                      minHeight: 48,
                      borderRadius: 1,
                      px: collapsed ? 1 : 1.5,
                      py: 1,
                      mb: 0.5,
                      justifyContent: collapsed ? 'center' : 'flex-start',
                      '&:hover': {
                        bgcolor: 'action.hover',
                        borderRadius: 1
                      },
                      '&.Mui-selected': {
                        bgcolor: 'action.selected',
                        color: 'text.primary',
                        borderRadius: 1,
                        '&:hover': {
                          bgcolor: 'action.selected'
                        },
                        '& .MuiListItemIcon-root': {
                          color: 'text.primary'
                        }
                      }
                    }}
                  >
                    {item.icon && (
                      <ListItemIcon sx={{ 
                        minWidth: collapsed ? 0 : 40,
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        mr: collapsed ? 0 : 1
                      }}>
                        {item.icon}
                      </ListItemIcon>
                    )}
                    {!collapsed && (
                      <ListItemText 
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: '0.875rem',
                          fontWeight: item.selected ? 500 : 400,
                          fontFamily: 'inherit'
                        }}
                      />
                    )}
                    {item.suffix && !collapsed && (
                      <Box
                        sx={{
                          height: 20,
                          px: 0.75,
                          py: 0,
                          fontSize: '0.7rem',
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        {item.suffix}
                      </Box>
                    )}
                  </ListItemButton>
                </ListItem>
                {/* Expanded Content (如 CategorySidebar) - 收起时隐藏 */}
                {!collapsed && item.expandedContent && (
                  <Box>
                    {item.expandedContent}
                  </Box>
                )}
              </React.Fragment>
            );
          })
          )}

          {/* Children items (如 Forum, Notebooks) - 作为普通菜单项 */}
          {children && React.Children.map(children, (child, index) => {
            if (React.isValidElement(child)) {
              // 每个 child 应该是一个 ListItemButton，包装在 ListItem 中
              return (
                <ListItem key={`child-${index}`} disablePadding>
                  {React.cloneElement(child, {
                    sx: {
                      minHeight: 48,
                      borderRadius: 1,
                      px: collapsed ? 1 : 1.5,
                      py: 1,
                      mb: 0.5,
                      justifyContent: collapsed ? 'center' : 'flex-start',
                      '&:hover': {
                        bgcolor: 'action.hover',
                        borderRadius: 1
                      },
                      '&.Mui-selected': {
                        bgcolor: 'action.selected',
                        color: 'text.primary',
                        borderRadius: 1,
                        '&:hover': {
                          bgcolor: 'action.selected'
                        },
                        '& .MuiListItemIcon-root': {
                          color: 'text.primary'
                        }
                      },
                      '& .MuiListItemIcon-root': {
                        minWidth: collapsed ? 0 : 40,
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        mr: collapsed ? 0 : 1
                      },
                      '& .MuiListItemText-root': {
                        display: collapsed ? 'none' : 'block'
                      },
                      ...child.props.sx
                    }
                  })}
                </ListItem>
              );
            }
            return null;
          })}
        </List>
      </Box>

      <Divider />

      {/* User info at bottom - 展开时显示姓名/角色/设置，收起时只显示头像 */}
      <Box sx={{ flexShrink: 0, p: collapsed ? 1.5 : 2, display: 'flex', justifyContent: collapsed ? 'center' : 'flex-start', alignItems: 'center', flexDirection: collapsed ? 'column' : 'row', gap: collapsed ? 1 : 0 }}>
        {collapsed ? (
          <>
            <UserAvatar
              user={user}
              size={40}
              sx={{ fontSize: '1rem' }}
            />
            <IconButton
              size="small"
              onClick={handleLogoutClick}
              sx={{
                color: 'text.secondary',
                '&:hover': { color: 'error.main', bgcolor: 'action.hover' }
              }}
              title="退出登录"
            >
              <LogoutIcon fontSize="small" />
            </IconButton>
          </>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0 }}>
            <Box sx={{ mr: 'auto', minWidth: 0, flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: '16px' }} noWrap>
                {courseName || '请选择课程'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                {courseTermCode || (userInfo?.role || '用户账号')}
              </Typography>
            </Box>
            {(userInfo?.role === '学生账号' || user?.role === 'student' ||
              userInfo?.role === '教师账号' || user?.role === 'teacher') ? (
              <IconButton
                size="small"
                onClick={() => {
                  if (onSettingsClick) {
                    onSettingsClick();
                  } else if (typeof window !== 'undefined') {
                    const path = (userInfo?.role === '教师账号' || user?.role === 'teacher')
                      ? '/teacher/settings'
                      : '/settings';
                    window.location.href = path;
                  }
                }}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'primary.main',
                    bgcolor: 'action.hover'
                  }
                }}
                title="设置"
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            ) : null}
            <IconButton
              size="small"
              onClick={handleLogoutClick}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: 'error.main',
                  bgcolor: 'action.hover'
                }
              }}
              title="退出登录"
            >
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>
      </Box>

      {/* 退出登录确认框 */}
      <Dialog open={logoutConfirmOpen} onClose={handleLogoutCancel} maxWidth="xs" fullWidth>
        <DialogTitle>确认退出</DialogTitle>
        <DialogContent>
          <Typography>确定要退出登录吗？</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogoutCancel}>取消</Button>
          <Button onClick={handleLogoutConfirm} variant="contained" color="error" startIcon={<LogoutIcon />}>
            确认退出
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default MuiSidebar;
